import React, { useState } from "react";
import { Button, Modal, Alert } from "react-bootstrap";
import { useAppDispatch } from "../../app/hooks";
import {
  getOrderEmailHistory,
  getVerificationPreview,
  sendVerificationEmail,
} from "../../app/services/orderService";
import EmailHistoryAccordion from "../email/emailHistoryList";
import { EmailHistory } from "../../models/responses/emailHistory";
import { Customer } from "../../models/responses/customer";
import { ExclamationDiamondFill } from "react-bootstrap-icons";

interface StandardPriceProposalProps {
  instance: any;
  account: any;
  orderId: string | undefined;
  emailHistory: EmailHistory[] | null;
  isWaitingSendVerificationEmailResponse: boolean;
  isWaitingVerificationPreviewResponse: boolean;
  customer: Customer | null;
}

const StandardPriceProposal: React.FC<StandardPriceProposalProps> = ({
  instance,
  account,
  orderId,
  emailHistory,
  isWaitingSendVerificationEmailResponse,
  isWaitingVerificationPreviewResponse,
  customer,
}) => {
  const dispatch = useAppDispatch();
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const onEmailSuccess = (success: boolean | null) => {
    if (success) {
      setShowEmailSuccess(true);
      setShowEmailError(false);
      setTimeout(() => setShowEmailSuccess(false), 4000);
    } else {
      setShowEmailError(true);
      setShowEmailSuccess(false);
      setTimeout(() => setShowEmailError(false), 4000);
    }
  };

  const handleVerificationProposal = async () => {
    await dispatch(
      sendVerificationEmail(instance, account, orderId, onEmailSuccess),
    );
    dispatch(getOrderEmailHistory(instance, account, orderId));
  };

  const handleResendVerificationProposal = () => {
    setShowResendModal(true);
  };

  const confirmResendVerificationProposal = async () => {
    setShowResendModal(false);
    await dispatch(sendVerificationEmail(instance, account, orderId, onEmailSuccess));
    dispatch(getOrderEmailHistory(instance, account, orderId));
  };

  const cancelResendVerificationProposal = () => {
    setShowResendModal(false);
  };

  const handleVerificationPreview = async () => {
    await dispatch(getVerificationPreview(instance, account, orderId));
  };

  return (
    <div className="mt-3">
      <Button
        variant="primary"
        onClick={handleVerificationPreview}
        disabled={isWaitingVerificationPreviewResponse}
        className="me-3"
      >
        Förhandsgranska verifikation
      </Button>
      {(!emailHistory || emailHistory.length === 0) && (
        <>
          <Button
            variant={!customer?.email ? "danger" : "primary"}
            onClick={handleVerificationProposal}
            disabled={
              isWaitingSendVerificationEmailResponse || !customer?.email
            }
          >
            {!customer?.email && (
              <>
                <ExclamationDiamondFill className="me-1" />
                Kunden saknar e-postadress, vänligen uppdatera
                kundinformationen.
              </>
            )}
            {customer?.email && (
              <>Skicka verifikation till {customer?.name ?? "kunden"}</>
            )}
          </Button>
        </>
      )}
      {emailHistory && emailHistory.length > 0 && (
        <>
          <Button
            variant="primary"
            onClick={handleResendVerificationProposal}
            disabled={isWaitingSendVerificationEmailResponse}
          >
            Skicka verifikation igen till {customer?.name ?? "kunden"}
          </Button>
          <Modal
            show={showResendModal}
            onHide={cancelResendVerificationProposal}
          >
            <Modal.Header closeButton>
              <Modal.Title>Skicka verifikation igen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Det finns redan en skickad verifikation för denna order.
              <br />
              Är du säker på att du vill skicka verifikationen igen till{" "}
              {customer?.name ?? "kunden"}?
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={cancelResendVerificationProposal}
              >
                Avbryt
              </Button>
              <Button
                variant="primary"
                onClick={confirmResendVerificationProposal}
              >
                Skicka ändå
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
      <Alert
        key="email-success"
        variant="success"
        show={showEmailSuccess}
        className="mt-3"
      >
        <p>Pdf skickad!</p>
      </Alert>
      <Alert
        key="email-error"
        variant="danger"
        show={showEmailError}
        className="mt-3"
      >
        <p>
          Något gick fel vid sändning av verifikation, vänligen försök igen.
        </p>
      </Alert>
      <div className="mt-3">
        <EmailHistoryAccordion emailHistory={emailHistory} />
      </div>
    </div>
  );
};

export default StandardPriceProposal;
